import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import FileUpload from "@mui/icons-material/FileUpload";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import type { ChangeEvent, FC } from "react";
import { useCallback, useRef, useState } from "react";
import { INSERT_IMAGE_FILES_COMMAND } from "../../../types/commands";
import { URLInput } from "../image-button";
import { UploadImageInput } from "./upload-image-input";

export const ContentLibraryEmptyModal: FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const [editor] = useLexicalComposerContext();
  const inputFile = useRef<HTMLInputElement>(null);

  const insertImage = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        editor.dispatchCommand(INSERT_IMAGE_FILES_COMMAND, {
          files: e.target.files,
        });
      }
      e.target.value = "";
      onClose();
    },
    [editor, onClose],
  );

  const [urlAnchorEl, setUrlAnchorEl] = useState<Element | null>(null);

  const closeLinkUrl = useCallback(() => {
    setUrlAnchorEl(null);
    onClose();
  }, [onClose]);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="lg">
        <DialogTitle my={2}>Content Library</DialogTitle>
        <DialogContent>
          <Stack gap={4} textAlign="center" maxWidth="md" mx="auto" alignItems="center" my={4}>
            <Stack gap={2} direction="row" alignItems="center">
              <svg xmlns="http://www.w3.org/2000/svg" width="50px" viewBox="0 0 24 24">
                <defs>
                  <linearGradient id="Gradient1">
                    <stop className="stop1" offset="8.45%" />
                    <stop className="stop2" offset="106.53%" />
                  </linearGradient>
                </defs>
                <style>
                  {`
              .stop1 {
                  stop-color: #00B6EF;
              }
              .stop2 {
                  stop-color: #DF1C8E;
              }
              `}
                </style>
                <path
                  d="M9 13h10l-3.45-4.5l-2.3 3l-1.55-2zm-6 8q-.825 0-1.412-.587T1 19V6h2v13h17v2zm4-4q-.825 0-1.412-.587T5 15V4q0-.825.588-1.412T7 2h5l2 2h7q.825 0 1.413.588T23 6v9q0 .825-.587 1.413T21 17zm0-2h14V6h-7.825l-2-2H7zm0 0V4z"
                  fill="url(#Gradient1)"
                />
              </svg>
              <Typography variant="h6">You haven’t saved any content yet</Typography>
            </Stack>
            <Typography variant="body1">
              You can now create video call backgrounds, images, FAQs and more using ChangeEngine’s
              AI. Use “Home” from the main navigation to do this.
            </Typography>
            <Stack gap={2} direction="row">
              <Button
                data-analytics-id="editor-empty-content-library-upload-image"
                startIcon={<FileUpload />}
                variant="contained"
                onClick={() => {
                  inputFile?.current?.click();
                }}
              >
                Upload an Image
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button data-analytics-id="editor-empty-content-cancel" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-analytics-id="editor-empty-content-insert-image"
            type="button"
            disabled={true}
          >
            Insert selected image
          </Button>
        </DialogActions>
      </Dialog>
      <UploadImageInput inputFileRef={inputFile} onChange={insertImage} />
      <Popover open={Boolean(urlAnchorEl)} anchorEl={urlAnchorEl} onClose={closeLinkUrl}>
        <URLInput onClose={closeLinkUrl} />
      </Popover>
    </>
  );
};
